import {
  DEAL_FIELD,
  DealStatus,
  DealType,
  STORE_EVENTS
} from '@/constants/storeEvents'
import type { AffectedDeal, Deal } from '@/ducks/offers/offers.types'
import { trackStoreEvent } from '@/utils/storeEvents.utils'

/**
 * Helpers for the deal events of EXSR-7992 (STORE_DEAL_CREATED / UPDATED /
 * STATUS_CHANGE / ITEM_OOS). Call them only once the API confirmed the action.
 * They never throw (trackStoreEvent does not either), so they cannot turn a
 * successful save into an error toast.
 */

/**
 * One STORE_DEAL_CREATED per deal the create response reports. A backend that
 * does not send `deals` yet gives nothing to report: without a deal_id the event
 * would be meaningless, so it is skipped rather than sent half empty.
 */
export function trackDealsCreated(deals: Deal[] | null | undefined): void {
  if (!Array.isArray(deals)) return

  deals.forEach(deal => {
    if (!deal?.dealId) return

    trackStoreEvent(STORE_EVENTS.DEAL_CREATED, {
      deal_id: deal.dealId,
      deal_type: deal.dealType,
      start_at: deal.startAt,
      end_at: deal.endAt
    })
  })
}

/** STORE_DEAL_UPDATED, unless the save changed nothing. */
export function trackDealUpdated(
  dealId: string | null | undefined,
  dealType: DealType,
  updatedFields: string[]
): void {
  if (!dealId || updatedFields.length === 0) return

  trackStoreEvent(STORE_EVENTS.DEAL_UPDATED, {
    deal_id: dealId,
    deal_type: dealType,
    updated_fields: updatedFields
  })
}

/** STORE_DEAL_STATUS_CHANGE. Deleting a deal is not a status change. */
export function trackDealStatusChange(
  dealId: string | null | undefined,
  dealType: DealType,
  status: DealStatus
): void {
  if (!dealId) return

  trackStoreEvent(STORE_EVENTS.DEAL_STATUS_CHANGE, {
    deal_id: dealId,
    deal_type: dealType,
    STATUS: status
  })
}

/**
 * One STORE_DEAL_ITEM_OOS per active deal the hide response reports as
 * affected. Nothing when the list is absent (old backend) or empty.
 */
export function trackDealsItemOos(
  affectedDeals: AffectedDeal[] | null | undefined
): void {
  if (!Array.isArray(affectedDeals)) return

  affectedDeals.forEach(deal => {
    if (!deal?.dealId) return

    trackStoreEvent(STORE_EVENTS.DEAL_ITEM_OOS, {
      deal_id: deal.dealId,
      deal_type: deal.dealType,
      item_id: deal.itemId,
      item_name: deal.itemName
    })
  })
}

// --- updated_fields ----------------------------------------------------------
// The forms are not react-hook-form: they keep their initial values (location
// state or props) next to the current ones. Each function below compares them
// in the shape of the request body, and returns the DEAL_FIELD names whose value
// changed. Numbers compare as the form sends them (Number(x) || 0, so a legacy
// page passing 10 and a state holding '10' are equal); `undefined` equals ''.

type Raw = string | number | boolean | null | undefined

const same = (before: Raw, after: Raw) =>
  String(before ?? '') === String(after ?? '')

const asSentNumber = (value: Raw) => Number(value) || 0

const changedFields = (fields: Array<[string, Raw, Raw]>): string[] =>
  fields
    .filter(([, before, after]) => !same(before, after))
    .map(([name]) => name)

export type FoodOfferBody = { amount?: Raw; offerValidUntil?: Raw }

export const changedFoodOfferFields = (
  before: FoodOfferBody,
  after: FoodOfferBody
): string[] =>
  changedFields([
    [
      DEAL_FIELD.AMOUNT,
      asSentNumber(before.amount),
      asSentNumber(after.amount)
    ],
    [
      DEAL_FIELD.OFFER_VALID_UNTIL,
      before.offerValidUntil,
      after.offerValidUntil
    ]
  ])

export type CartDiscountBody = {
  discountType?: Raw
  amountPercentage?: Raw
  maxOff?: Raw
  expirationTime?: Raw
  targetAmount?: Raw
}

export const changedCartDiscountFields = (
  before: CartDiscountBody,
  after: CartDiscountBody
): string[] =>
  changedFields([
    [DEAL_FIELD.DISCOUNT_TYPE, before.discountType, after.discountType],
    [
      DEAL_FIELD.AMOUNT_PERCENTAGE,
      asSentNumber(before.amountPercentage),
      asSentNumber(after.amountPercentage)
    ],
    [
      DEAL_FIELD.MAX_OFF,
      asSentNumber(before.maxOff),
      asSentNumber(after.maxOff)
    ],
    [DEAL_FIELD.EXPIRATION_TIME, before.expirationTime, after.expirationTime],
    [
      DEAL_FIELD.TARGET_AMOUNT,
      asSentNumber(before.targetAmount),
      asSentNumber(after.targetAmount)
    ]
  ])

export type BuyXGetYBody = {
  itemXId?: Raw
  itemYId?: Raw
  nbrItemsPurchased?: Raw
  nbrItemsOffer?: Raw
  discountPercentage?: Raw
  expirationTime?: Raw
  cumulatif?: Raw
}

export const changedBuyXGetYFields = (
  before: BuyXGetYBody,
  after: BuyXGetYBody
): string[] =>
  changedFields([
    [DEAL_FIELD.ITEM_X_ID, before.itemXId, after.itemXId],
    [DEAL_FIELD.ITEM_Y_ID, before.itemYId, after.itemYId],
    [
      DEAL_FIELD.NBR_ITEMS_PURCHASED,
      asSentNumber(before.nbrItemsPurchased),
      asSentNumber(after.nbrItemsPurchased)
    ],
    [
      DEAL_FIELD.NBR_ITEMS_OFFER,
      asSentNumber(before.nbrItemsOffer),
      asSentNumber(after.nbrItemsOffer)
    ],
    [
      DEAL_FIELD.DISCOUNT_PERCENTAGE,
      asSentNumber(before.discountPercentage),
      asSentNumber(after.discountPercentage)
    ],
    [DEAL_FIELD.EXPIRATION_TIME, before.expirationTime, after.expirationTime],
    [DEAL_FIELD.CUMULATIF, Boolean(before.cumulatif), Boolean(after.cumulatif)]
  ])
